import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Smartphone } from 'lucide-react';
import { useLocalization } from '../../contexts/LocalizationContext';

const ANDROID_PACKAGE = 'com.optimizeware.morafaa';
const APP_DEEP_LINK = 'morafaa://store/ad-free';
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;

/**
 * Public Kashier return URL for Ad-Free (and future Store products).
 * Must stay outside auth guards — mobile checkout has no web session.
 *
 * Backend sets merchantRedirect → /payment/result
 *
 * Important: do NOT set S.browser_fallback_url to Play Store.
 * Chrome Custom Tabs / Chrome often fail to resolve the installed app
 * (especially debug builds) and then dump the user on Play listing.
 * Play remains a manual last-resort link only.
 */
const PaymentResultBridgePage: React.FC = () => {
  const [params] = useSearchParams();
  const { language } = useLocalization();
  const isAr = language === 'ar';

  const status = (
    params.get('paymentStatus') ||
    params.get('status') ||
    params.get('orderStatus') ||
    ''
  ).toUpperCase();
  const success =
    status === 'SUCCESS' ||
    status === 'CAPTURED' ||
    status === 'PAID' ||
    status === 'APPROVED' ||
    params.get('success') === 'true';

  /** Intent that opens the installed app — no Play Store fallback. */
  const openAppHref = useMemo(() => {
    return (
      `intent://store/ad-free#Intent;` +
      `scheme=morafaa;` +
      `package=${ANDROID_PACKAGE};` +
      `action=android.intent.action.VIEW;` +
      `category=android.intent.category.BROWSABLE;` +
      `end`
    );
  }, []);

  useEffect(() => {
    // Always try to return to the app after checkout (success or not).
    // Custom scheme first (works when app is installed); then intent:// without Play fallback.
    const t1 = window.setTimeout(() => {
      window.location.href = APP_DEEP_LINK;
    }, 600);
    const t2 = window.setTimeout(() => {
      window.location.href = openAppHref;
    }, 1600);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [openAppHref]);

  const copy = success
    ? {
        title: isAr ? 'تم الدفع بنجاح' : 'Payment successful',
        body: isAr
          ? 'جارٍ فتح تطبيق مرافعة. إذا لم يفتح تلقائياً، اضغط الزر بالأسفل ثم حدّث حالة الاشتراك.'
          : 'Opening the Morafaa app. If it does not open, tap the button below, then refresh subscription status.',
        cta: isAr ? 'فتح تطبيق مرافعة' : 'Open Morafaa app',
      }
    : {
        title: isAr ? 'تم الإرجاع من الدفع' : 'Returned from payment',
        body: isAr
          ? 'ارجع إلى التطبيق. إن لم يكتمل الدفع يمكنك المحاولة مرة أخرى من المتجر.'
          : 'Return to the app. If payment did not complete, try again from the Store.',
        cta: isAr ? 'فتح تطبيق مرافعة' : 'Open Morafaa app',
      };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: '#0F172A' }}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl text-center space-y-5">
        {success ? (
          <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" aria-hidden />
        ) : (
          <XCircle className="w-14 h-14 text-red-500 mx-auto" aria-hidden />
        )}
        <h1 className="text-2xl font-bold text-slate-900">{copy.title}</h1>
        <p className="text-slate-600 text-sm leading-relaxed">{copy.body}</p>
        <a
          href={APP_DEEP_LINK}
          className="inline-flex items-center justify-center gap-2 w-full rounded-xl bg-[#C9A14B] text-white font-semibold py-3 px-4"
        >
          <Smartphone className="w-5 h-5" aria-hidden />
          {copy.cta}
        </a>
        <a href={openAppHref} className="block text-sm text-slate-500 underline">
          {isAr ? 'رابط بديل (Intent)' : 'Alternate open link'}
        </a>
        <p className="text-xs text-slate-400">
          {isAr
            ? 'التفعيل يتم عبر السيرفر تلقائياً — حدّث الحالة داخل التطبيق.'
            : 'Activation is server-side — refresh status inside the app.'}
        </p>
        <a href={PLAY_STORE_URL} className="block text-xs text-slate-300 underline">
          {isAr ? 'التطبيق غير مثبت؟ Google Play' : 'App not installed? Google Play'}
        </a>
      </div>
    </div>
  );
};

export default PaymentResultBridgePage;
